/**
 * 设置：模型路线（每个角色一条，不自动换路线）、ChatGPT 订阅登录、外部密钥、外观。
 * 密钥只写入本机密钥目录（权限 0600），不进数据库、不进日志、不回显。
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import clsx from "clsx";
import { useState } from "react";
import { DevicesCard } from "@/features/devices/DevicesCard";
import { FeedbackList } from "@/features/devices/FeedbackList";
import { KbAgentsCard } from "@/features/devices/KbAgents";
import { PhoneCard } from "@/features/devices/PhoneCard";
import { errorMessage } from "@/shared/api/client";
import { codexComplete, codexLogin, codexLogout, putSecret } from "@/shared/api/gen/sdk.gen";
import { keys, useAction, useModels } from "@/shared/api/hooks";
import { useScene, useUi } from "@/shared/scene";
import { Button, Card, ErrorNote, PageHeader, Pill, TextInput } from "@/shared/ui";

export const Route = createFileRoute("/settings")({ component: Settings });

const ROLE_TEXT: Record<string, string> = {
  tutor: "导师（答疑）",
  teacher: "插注（解释选中的内容）",
  checker: "判分与核对",
  planner: "规划（知识地图）",
  fast: "快速任务",
  vision: "看图",
  embed: "向量索引",
  rerank: "重排",
};

const SECRET_TEXT: Record<string, string> = {
  siliconflow: "硅基流动（向量与重排）",
  openrouter: "OpenRouter",
  deepseek: "DeepSeek",
};

function Settings() {
  useScene({ page: "设置" });
  const models = useModels();
  const theme = useUi((s) => s.theme);
  const setTheme = useUi((s) => s.setTheme);

  return (
    <div>
      <PageHeader title="设置" />
      <div className="flex max-w-[860px] flex-col gap-4 px-4 md:px-7">
        <ChatModelsCard
          current={models.data?.routes.find((r) => r.role === "tutor")?.provider ?? ""}
          deepseekKey={models.data?.secrets.deepseek ?? false}
        />
        {models.data?.routes.find((r) => r.role === "tutor")?.provider === "openai-codex" ? (
          <CodexCard
            loggedIn={models.data?.codex_logged_in ?? false}
            pending={models.data?.login_pending ?? false}
          />
        ) : null}
        <PhoneCard />
        <DevicesCard />
        <KbAgentsCard />
        <FeedbackList />
        <Card className="p-5">
          <h2 className="mb-1 font-semibold">模型路线</h2>
          <p className="mb-3 text-[12.5px] text-text-2">
            每个角色一条路线。路线不可用时直接告诉你，不会悄悄换成别的模型。
          </p>
          <ul className="divide-y divide-border text-[13px]">
            {(models.data?.routes ?? []).map((r) => (
              <li key={r.role} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2">
                <span className="w-28 shrink-0">{ROLE_TEXT[r.role] ?? r.role}</span>
                <span className="min-w-0 flex-1 font-mono text-[12.5px] [overflow-wrap:anywhere]">
                  {r.provider === "demo" ? "演示模型" : r.model}
                  {r.reasoning ? <span className="text-text-3"> · {r.reasoning}</span> : null}
                </span>
                <span className="text-text-2">
                  {r.subscription ? "订阅" : r.provider === "demo" ? "本地" : "按量"}
                </span>
                {r.ready ? <Pill tone="ok">可用</Pill> : <Pill tone="warn">{r.reason || "不可用"}</Pill>}
              </li>
            ))}
          </ul>
        </Card>
        <Card className="flex flex-col gap-3 p-5">
          <h2 className="font-semibold">外部密钥</h2>
          {Object.entries(models.data?.secrets ?? {}).map(([name, saved]) => (
            <SecretRow key={name} name={name} saved={saved} />
          ))}
        </Card>
        <Card className="flex flex-col gap-2 p-5">
          <h2 className="font-semibold">外观</h2>
          <fieldset className="flex gap-1">
            <legend className="sr-only">外观</legend>
            {(
              [
                ["system", "跟随系统"],
                ["light", "晨"],
                ["dark", "昏"],
              ] as const
            ).map(([value, label]) => (
              <label
                key={value}
                className={clsx(
                  "flex h-8 cursor-pointer items-center rounded-s px-3 text-[13px] has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-[var(--focus)]",
                  theme === value ? "bg-action font-semibold text-action-text" : "bg-surface-2 text-text-2",
                )}
              >
                <input
                  type="radio"
                  name="theme"
                  value={value}
                  checked={theme === value}
                  onChange={() => setTheme(value)}
                  className="sr-only"
                />
                {label}
              </label>
            ))}
          </fieldset>
        </Card>
      </div>
    </div>
  );
}

const PRESETS = [
  {
    value: "chatgpt",
    provider: "openai-codex",
    title: "ChatGPT 订阅",
    detail: "用你的订阅额度，不按次计费；要先登录。",
  },
  {
    value: "deepseek",
    provider: "deepseek",
    title: "DeepSeek API",
    detail: "deepseek-flash：能看图、按量计费（很便宜）；要在下面“外部密钥”里填 DeepSeek 密钥。",
  },
] as const;

/** 插注、答疑等对话类角色一起换；嵌入与重排另走硅基流动，不跟着换。 */
function ChatModelsCard({ current, deepseekKey }: { current: string; deepseekKey: boolean }) {
  const qc = useQueryClient();
  const change = useAction<{ preset: string }>("system.use_chat_models");
  return (
    <Card className="flex flex-col gap-3 p-5">
      <div>
        <h2 className="font-semibold">对话模型</h2>
        <p className="text-[12.5px] text-text-2">
          插注和答疑用哪个模型。换了以后马上生效，不会自动切回另一个。
        </p>
      </div>
      <fieldset className="flex flex-col gap-2">
        <legend className="sr-only">对话模型</legend>
        {PRESETS.map((p) => (
          <label
            key={p.value}
            className={clsx(
              "flex cursor-pointer gap-3 rounded-m border p-3 text-[13px] has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-[var(--focus)]",
              current === p.provider ? "border-action bg-surface-2" : "border-border",
            )}
          >
            <input
              type="radio"
              name="chat-models"
              value={p.value}
              checked={current === p.provider}
              disabled={change.isPending}
              onChange={() =>
                change.mutate(
                  { preset: p.value },
                  { onSettled: () => qc.invalidateQueries({ queryKey: keys.models }) },
                )
              }
              className="mt-0.5 accent-[var(--action)]"
            />
            <span className="flex flex-col gap-0.5">
              <span className="font-semibold">{p.title}</span>
              <span className="text-text-2">{p.detail}</span>
              {p.value === "deepseek" && current === p.provider && !deepseekKey ? (
                <span className="text-warn">还没填 DeepSeek 密钥：插注和答疑暂时用不了。</span>
              ) : null}
            </span>
          </label>
        ))}
      </fieldset>
      {change.error ? <ErrorNote>{errorMessage(change.error)}</ErrorNote> : null}
    </Card>
  );
}

function CodexCard({ loggedIn, pending }: { loggedIn: boolean; pending: boolean }) {
  const qc = useQueryClient();
  const [url, setUrl] = useState<string | null>(null);
  const login = useMutation({
    mutationFn: async () => (await codexLogin()).data as { authorization_url: string },
    onSuccess: (data) => {
      setUrl(data.authorization_url);
      window.open(data.authorization_url, "_blank", "noopener");
    },
  });
  const logout = useMutation({
    mutationFn: async () => (await codexLogout()).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.models }),
  });
  // 朝夕在服务器上时，授权后浏览器跳到的 localhost 网址打不开：把它整段粘贴回来完成登录
  const [callback, setCallback] = useState("");
  const complete = useMutation({
    mutationFn: async () => (await codexComplete({ body: { callback_url: callback } })).data,
    onSuccess: () => {
      setCallback("");
      setUrl(null);
      void qc.invalidateQueries({ queryKey: keys.models });
    },
  });
  return (
    <Card className="flex flex-col gap-2 p-5">
      <div className="flex items-center gap-2">
        <h2 className="font-semibold">ChatGPT 订阅</h2>
        {loggedIn ? <Pill tone="ok">已登录</Pill> : <Pill tone="warn">未登录</Pill>}
      </div>
      <p className="text-[13px] text-text-2">
        插注和答疑使用你的 ChatGPT 订阅额度，不按次计费。登录凭据只保存在朝夕所在机器的密钥目录。
      </p>
      {url && !loggedIn ? (
        <div className="flex flex-col gap-2 rounded-s bg-surface-2 px-3 py-2 text-[12.5px] text-text-2">
          <p>
            已在新窗口打开授权页（10 分钟内有效）。如果没有弹出，
            <a href={url} target="_blank" rel="noreferrer" className="underline">
              点这里打开
            </a>
            。朝夕在这台电脑上时，授权完成后这里会自动变成“已登录”。
          </p>
          <p>
            朝夕在服务器上时：授权完成后浏览器会跳到一个打不开的 <code>localhost:1455</code>{" "}
            网址，把地址栏里的整段网址复制下来，粘贴到这里。
          </p>
          <form
            className="flex flex-wrap gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (callback.trim()) complete.mutate();
            }}
          >
            <TextInput
              aria-label="授权后跳到的网址"
              className="min-w-0 flex-1"
              placeholder="http://localhost:1455/auth/callback?code=…"
              value={callback}
              onChange={(e) => setCallback(e.target.value)}
            />
            <Button type="submit" busy={complete.isPending} disabled={!callback.trim()}>
              完成登录
            </Button>
          </form>
        </div>
      ) : null}
      {login.error || logout.error || complete.error ? (
        <ErrorNote>{errorMessage(login.error ?? logout.error ?? complete.error)}</ErrorNote>
      ) : null}
      <div className="flex gap-2">
        {loggedIn ? (
          <Button variant="ghost" busy={logout.isPending} onClick={() => logout.mutate()}>
            退出登录
          </Button>
        ) : (
          // 刷新页面后上一次登录可能还在等：再点一次就重新开始（服务器会作废上一次）
          <Button variant="primary" busy={login.isPending} onClick={() => login.mutate()}>
            {pending && !url ? "重新登录 ChatGPT 订阅" : "登录 ChatGPT 订阅"}
          </Button>
        )}
      </div>
    </Card>
  );
}

function SecretRow({ name, saved }: { name: string; saved: boolean }) {
  const qc = useQueryClient();
  const [value, setValue] = useState("");
  const save = useMutation({
    mutationFn: async () => (await putSecret({ path: { name }, body: { value } })).data,
    onSuccess: () => {
      setValue("");
      void qc.invalidateQueries({ queryKey: keys.models });
    },
  });
  return (
    <form
      className="flex flex-col gap-1.5"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <div className="flex items-center gap-2 text-[13px]">
        <span className="font-medium">{SECRET_TEXT[name] ?? name}</span>
        {saved ? <Pill tone="ok">已保存</Pill> : <Pill>未设置</Pill>}
      </div>
      <div className="flex gap-2">
        <TextInput
          type="password"
          autoComplete="off"
          aria-label={`${SECRET_TEXT[name] ?? name} 密钥`}
          placeholder={saved ? "输入新密钥以替换" : "粘贴 API 密钥"}
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
        <Button type="submit" busy={save.isPending} disabled={!value.trim()}>
          保存
        </Button>
      </div>
      {save.error ? <ErrorNote>{errorMessage(save.error)}</ErrorNote> : null}
    </form>
  );
}
